import type { DemoData, PriceToleranceRule, TaxonomyProductMapping } from "../../domain/types";
import type { RuleEvaluationResult } from "../../domain/ruleEvaluation";
import type { RuleSimplifiedLogicEdit, RuleSummaryEditModel, RuleSummaryPresentation } from "../../domain/ruleSummary";
import type { ExecutableStorageBackedConfig } from "./ExecutableStorageBackedConfig";
import type { RuleSuggestionCommandPort } from "./RuleSuggestionCommandPort";

export type { ExecutableStorageBackedConfig } from "./ExecutableStorageBackedConfig";

export type RuleIntegrationEvaluationContext<RuleDefinition = unknown> = {
    definition?: RuleDefinition;
};

export type RuleConfigFactoryContext = {
    data?: DemoData;
    priceToleranceRules?: PriceToleranceRule[];
    taxonomyMappings?: TaxonomyProductMapping[];
};

export type RuleConfigFactoryPort<Config extends ExecutableStorageBackedConfig = ExecutableStorageBackedConfig> = {
    createConfig: (context?: RuleConfigFactoryContext) => Config;
};

export type RuleStorageCodecPort<Config extends ExecutableStorageBackedConfig = ExecutableStorageBackedConfig> = {
    serialize: (config: Config) => object;
    hydrate: (storageData: object) => Config;
};

export type RuleRowMapperPort<DemoRow = unknown, RuntimeRow = DemoRow> = {
    toRuntimeRow: (row: DemoRow) => RuntimeRow;
};

export type RuleEvaluatorPort<
    Config extends ExecutableStorageBackedConfig = ExecutableStorageBackedConfig,
    RuntimeRow = unknown,
    RuleDefinition = unknown,
> = {
    evaluate: (
        config: Config,
        row: RuntimeRow,
        context?: RuleIntegrationEvaluationContext<RuleDefinition>,
    ) => RuleEvaluationResult;
};

export type RuleSummaryPresenterPort<
    Config extends ExecutableStorageBackedConfig = ExecutableStorageBackedConfig,
    RuleDefinition = unknown,
> = {
    renderSummary: (config: Config, context?: RuleIntegrationEvaluationContext<RuleDefinition>) => RuleSummaryPresentation;
};

export type RuleSummaryEditPort<Config extends ExecutableStorageBackedConfig = ExecutableStorageBackedConfig> = {
    getEditorModel: (config: Config, context?: RuleIntegrationEvaluationContext) => RuleSummaryEditModel | null;
    applyEdit: (config: Config, edit: RuleSimplifiedLogicEdit) => Config;
};

export type RuleDemoDataProjection = {
    priceToleranceRules?: PriceToleranceRule[];
    taxonomyMappings?: TaxonomyProductMapping[];
};

export type RuleDemoProjectionPort<Config extends ExecutableStorageBackedConfig = ExecutableStorageBackedConfig> = {
    projectDemoData: (config: Config) => RuleDemoDataProjection;
};

export type RuleIntegrationPorts<
    Config extends ExecutableStorageBackedConfig = ExecutableStorageBackedConfig,
    DemoRow = unknown,
    RuntimeRow = DemoRow,
    RuleDefinition = unknown,
> = {
    configFactory: RuleConfigFactoryPort<Config>;
    storageCodec: RuleStorageCodecPort<Config>;
    rowMapper: RuleRowMapperPort<DemoRow, RuntimeRow>;
    evaluator: RuleEvaluatorPort<Config, RuntimeRow, RuleDefinition>;
    summaryPresenter?: RuleSummaryPresenterPort<Config, RuleDefinition>;
    simplifiedLogicEditor?: RuleSummaryEditPort<Config>;
    demoDataProjection?: RuleDemoProjectionPort<Config>;
    suggestionCommands?: RuleSuggestionCommandPort<Config>;
};

export const identityRuleRowMapper = <Row>(): RuleRowMapperPort<Row, Row> => ({
    toRuntimeRow: (row) => row,
});
